import React from 'react'
import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'

import {
    BarChart,
    GaugeChart,
    LineChart,
    PieChart,
    RadarChart,
    SankeyChart,
    TreemapChart,
} from 'echarts/charts'

import {
    GridComponent,
    LegendComponent,
    MarkAreaComponent,
    MarkPointComponent,
    TitleComponent,
    ToolboxComponent,
    TooltipComponent,
} from 'echarts/components'
import { SVGRenderer, CanvasRenderer } from 'echarts/renderers'

import type { EChartsOption as EChartsOptionType } from 'echarts'
// import { first, last, indexOf } from "lodash";

import { EChartsOption } from 'echarts'
import { first, last, indexOf } from '@/utils/function'

echarts.use([
    MarkPointComponent,
    TitleComponent,
    TooltipComponent,
    GridComponent,
    MarkAreaComponent,
    SVGRenderer,
    LineChart,
    BarChart,
    PieChart,
    RadarChart,
    SankeyChart,
    TreemapChart,
    GaugeChart,
    LegendComponent,
    ToolboxComponent,
    CanvasRenderer,
])

/**
 * Chart Color Schemes - VCloud Portal Aligned
 *
 * IMPORTANT: Always use SCHEMES for chart colors. Do not hardcode hex values.
 * ESLint rule 'no-hardcoded-chart-colors' enforces this.
 *
 * Usage:
 *   import { SCHEMES } from '@/components/EchartCustomized'
 *   color: SCHEMES.vcloud[0]  // Blue (Primary)
 *   color: SCHEMES.vcloud     // Full VCloud palette (10 colors)
 *   color: SCHEMES.default    // Extended 16-color palette
 *   color: SCHEMES.primary    // Single primary color
 *   color: SCHEMES.success[0] // Semantic green
 */
export const SCHEMES = {
    // VCloud Portal palette (10 colors) - Primary chart palette
    vcloud: [
        '#3b82f6', // Blue (Primary)
        '#10b981', // Green
        '#f59e0b', // Amber
        '#ef4444', // Red
        '#8b5cf6', // Purple
        '#ec4899', // Pink
        '#14b8a6', // Teal
        '#f97316', // Orange
        '#84cc16', // Lime
        '#06b6d4', // Cyan
    ],
    // Brand-aligned primary palette
    brand: [
        '#1A237E', // Primary Navy
        '#CE1721', // Brand Red
        '#008556', // Success Green
        '#F19611', // Warning Orange
        '#3B82F6', // Info Blue
        '#9333EA', // Purple accent
        '#0891B2', // Cyan accent
        '#84CC16', // Lime accent
    ],
    // Extended palette for charts with many series (VCloud-aligned)
    default: [
        '#3b82f6', // Blue (Primary)
        '#98df8a', // Green
        '#f59e0b', // Amber
        '#ef4444', // Red
        '#8b5cf6', // Purple
        '#ec4899', // Pink
        '#14b8a6', // Teal
        '#f97316', // Orange
        '#84cc16', // Lime
        '#06b6d4', // Cyan
        '#1A237E', // Navy
        '#22C55E', // Emerald
        '#EAB308', // Yellow
        '#F43F5E', // Rose
        '#6366F1', // Indigo
        '#0891B2', // Cyan dark
    ],
    // Semantic colors (VCloud-aligned)
    success: ['#10b981', '#34D399', '#6EE7B7', '#A7F3D0'],
    warning: ['#f59e0b', '#FBBF24', '#FCD34D', '#FDE68A'],
    error: ['#ef4444', '#F87171', '#FCA5A5', '#FECACA'],
    info: ['#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE'],
    // VCloud semantic shortcuts
    primary: '#3b82f6',
    secondary: '#10b981',
    accent: '#f59e0b',
    danger: '#ef4444',
    // Mono gradients
    mono: {
        navy: ['#1A237E', '#3949AB', '#5C6BC0', '#7986CB', '#9FA8DA'],
        gray: ['#374151', '#6B7280', '#9CA3AF', '#D1D5DB', '#E5E7EB'],
    },
    // Single colors
    gray: '#E5E7EB',
    black: '#1F2937',
    backgroundOpacity: 'rgba(255,255,255,0.9)',
    gradient: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: '#E8E9F2' }, // Primary light
        { offset: 1, color: 'rgba(255,255,255,1)' },
    ]),
    // Legacy aliases (deprecated - migrate to vcloud/default)
    /** @deprecated Use SCHEMES.info instead */
    blue: ['#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#DBEAFE'],
    /** @deprecated Use SCHEMES.vcloud instead */
    material: [
        '#3b82f6',
        '#10b981',
        '#f59e0b',
        '#ef4444',
        '#8b5cf6',
        '#ec4899',
        '#14b8a6',
        '#f97316',
        '#84cc16',
        '#06b6d4',
    ],
}

export const TOOLTIP_OPTIONS: EChartsOptionType['tooltip'] = {
    showDelay: 0,
    hideDelay: 50,
    transitionDuration: 0,
    backgroundColor: SCHEMES.backgroundOpacity,
    borderColor: SCHEMES.black,
    borderRadius: 0,
    borderWidth: 1,
    padding: [8, 8, 8, 8],
    textStyle: {
        color: SCHEMES.black,
        decoration: 'none',
        fontSize: 13,
    },
    extraCssText:
        'box-shadow: none; font-family: "Google Sans",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
}

export const useMarkArea = (
    array: string[] = [],
    arrayOfZero: string[] = []
): [string | undefined, string | undefined] => {
    let markAreaStart: string | undefined, markAreaStop: string | undefined

    if (arrayOfZero.length !== 0) {
        markAreaStart = first(arrayOfZero)
        markAreaStop = last(arrayOfZero)

        const indexOfFirstDayGotZeroTimestamp = indexOf(array, markAreaStart)

        // Ensure valid index before accessing previous element
        if (indexOfFirstDayGotZeroTimestamp > 0) {
            markAreaStart = array[indexOfFirstDayGotZeroTimestamp - 1]
        }
    }

    return [markAreaStart, markAreaStop]
}

type EChartCustomizedProps = {
    options: EChartsOption
    [key: string]: any
}

const EChartCustomized: React.FC<EChartCustomizedProps> = ({
    options,
    ...props
}) => {
    return options ? (
        <ReactEChartsCore
            echarts={echarts}
            option={options}
            {...props}
            key={JSON.stringify(options?.series)}
        />
    ) : null
}

export default EChartCustomized
